import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { TranslocoService } from '@jsverse/transloco';

import { environment } from 'src/environments/environment';

import {
    CompetencyModule,
    CreateCompetencyModuleRequest,
    UpdateCompetencyModuleRequest,
    DeleteCompetencyModuleRequest,
    CompetencyModulesResponse,
    CompetencyUnit,
    CreateCompetencyUnitRequest,
    UpdateCompetencyUnitRequest,
    DeleteCompetencyUnitRequest,
    FavouriteRequest,
    CompetencyUnitsPagedResponse,
    CompetencyUnitType,
    AssessmentCategory,
    RoleOption,
    CertificateOption,
    EnrollmentsPagedResponse,
    EnrollmentFilterRequest,
    Person,
    EnrollmentCreateRequest,
    DeleteEnrollmentRequest,
    RegionOption,
    CountryOption,
    CompanyOption,
    GradeOption,
} from '../models/competency-module.model';
import { UserDefaultModel } from 'src/app/models/user-default-model';
import { SharedContentCategoryModel } from '../../settings/models/general/shared-content-category-model';
import { APIRes } from '../../idms/models/commonAPI.model';
import { User } from '../../management/models/organization/user.model';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { EnvironmentService } from 'src/app/services/environment.service';
@Injectable({
    providedIn: 'root',
})
export class CompetencyModuleApiService {
    private cdmsApi = environment.cdmsApi;
    private mgmtApi = environment.mgmtApi;
    private subVal = '';
    private logedInUserId = this.authService?.instance?.getActiveAccount()?.idTokenClaims?.oid;

    headers: HttpHeaders;
    userDefaults: UserDefaultModel[];
    sharedContentCategories: SharedContentCategoryModel[];
    userDetails: User;

    constructor(
        private http: HttpClient,
        private authService: MsalService,
        private EnvironmentService: EnvironmentService
    ) {
        this.subVal = this.EnvironmentService.getConfig().subValue;
        this.headers = new HttpHeaders({
            'Ocp-Apim-Subscription-Key': this.subVal,
            Authorization: `Bearer ${localStorage.getItem('msal.accessToken')}`,
        });
    }

    // api call to get all competency modules
    getAllCompetencyModules(start?: number, end?: number): Observable<APIRes<{ total: number; data: CompetencyModule[] }>> {
        this.addPostAndPutHeaders();
        if (start && end) {
            const params = new HttpParams().set('Start', start.toString()).set('End', end.toString());
            return this.http.get(`${this.cdmsApi}competencymodules`, { headers: this.headers, params }).pipe(catchError(this.handleError.bind(this)));
        }
        return this.http.get(`${this.cdmsApi}competencymodules`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to create new competency modules
    // createCompetencyModules(createRequests: CreateCompetencyModuleRequest[]): Observable<CompetencyModulesResponse> {
    //     this.addPostAndPutHeaders();
    //     return this.http.post(`${this.cdmsApi}competencymodules`, createRequests, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    // }

    // api call to create a single competency module
    createCompetencyModule(createRequest: CreateCompetencyModuleRequest): Observable<APIRes<CompetencyModulesResponse>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.cdmsApi}competencymodules`, [createRequest], { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to update existing competency modules
    // updateCompetencyModules(updateRequests: UpdateCompetencyModuleRequest[]): Observable<APIRes<CompetencyModulesResponse>> {
    //     this.addPostAndPutHeaders();
    //     return this.http.put(`${this.cdmsApi}competencymodules`, updateRequests, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    // }

    // api call to update a single competency module
    updateCompetencyModule(updateRequest: UpdateCompetencyModuleRequest): Observable<APIRes<CompetencyModulesResponse>> {
        this.addPostAndPutHeaders();
        return this.http.put(`${this.cdmsApi}competencymodules`, [updateRequest], { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to delete/deactivate competency modules
    // deleteCompetencyModules(deleteRequests: DeleteCompetencyModuleRequest[]): Observable<APIRes<CompetencyModulesResponse>> {
    //     this.addPostAndPutHeaders();
    //     return this.http.put(`${this.cdmsApi}competencymodules`, deleteRequests, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    // }

    // api call to delete/deactivate a single competency module
    deleteCompetencyModule(competencyModuleId: string): Observable<APIRes<CompetencyModulesResponse>> {
        const deleteRequest: DeleteCompetencyModuleRequest = {
            competencyModuleId,
            active: false,
        };
        this.addPostAndPutHeaders();
        return this.http.put(`${this.cdmsApi}competencymodules`, [deleteRequest], { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to get competency units by competency module id (supports pagination)
    getCompetencyUnits(competencyModuleId: string, start?: number, end?: number): Observable<APIRes<CompetencyUnitsPagedResponse>> {
        this.addPostAndPutHeaders();

        let params = new HttpParams().set('competencyModuleId', competencyModuleId);
        if (start != null && end != null) {
            params = params.set('start', start.toString()).set('end', end.toString());
        }

        return this.http.get(`${this.cdmsApi}competencyunits`, { headers: this.headers, params }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to get competency unit types
    getCompetencyUnitTypes(): Observable<APIRes<CompetencyUnitType[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}competencyunits/getcompetencyunittypes`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to get assessment categories
    getAssessmentCategories(): Observable<APIRes<AssessmentCategory[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}AssessmentCategories`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    createAssessmentCategories(payload: Array<{ name: string; index: number }>): Observable<APIRes<any>> {
        return this.postLookupChanges('assessmentcategories', payload);
    }

    updateAssessmentCategories(payload: Array<{ assessmentCategoryId: string; name: string; index: number }>): Observable<APIRes<any>> {
        return this.putLookupChanges('assessmentcategories', payload);
    }

    deleteAssessmentCategories(payload: Array<{ assessmentCategoryId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('assessmentcategories', payload);
    }

    // api call to get roles
    getRoles(): Observable<APIRes<RoleOption[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}roles`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to get grades
    getGrades(): Observable<APIRes<GradeOption[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}grades`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    createGrades(payload: Array<{ name: string }>): Observable<APIRes<any>> {
        return this.postLookupChanges('grades', payload);
    }

    updateGrades(payload: Array<{ gradeId: string; name: string }>): Observable<APIRes<any>> {
        return this.putLookupChanges('grades', payload);
    }

    deleteGrades(payload: Array<{ gradeId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('grades', payload);
    }

    // api call to get certificates
    getCertificates(): Observable<APIRes<CertificateOption[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}certificates`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to get enrollments by competency unit with filters
    getEnrollments(payload: EnrollmentFilterRequest): Observable<APIRes<EnrollmentsPagedResponse>> {
        this.addPostAndPutHeaders();
        return this.http
            .post(`${this.cdmsApi}enrollments/getenrollmentsbycompetencyunit`, payload, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // api call to create competency units for a competency module
    createCompetencyUnits(competencyModuleId: string, createRequests: CreateCompetencyUnitRequest[]): Observable<APIRes<CompetencyUnit[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .post(`${this.cdmsApi}competencyunits/${competencyModuleId}`, createRequests, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // api call to update competency units for a competency module
    updateCompetencyUnits(competencyModuleId: string, updateRequests: UpdateCompetencyUnitRequest[]): Observable<APIRes<CompetencyUnit[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .put(`${this.cdmsApi}competencyunits/${competencyModuleId}`, updateRequests, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // api call to delete/deactivate competency units for a competency module
    deleteCompetencyUnits(competencyModuleId: string, deleteRequests: DeleteCompetencyUnitRequest[]): Observable<APIRes<CompetencyUnit[]>> {
        this.addPostAndPutHeaders();
        return this.http
            .put(`${this.cdmsApi}competencyunits/${competencyModuleId}`, deleteRequests, { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // api call to delete/deactivate a single competency unit for a competency module
    deleteCompetencyUnit(competencyModuleId: string, competencyUnitId: string): Observable<APIRes<CompetencyUnit[]>> {
        const deleteRequest: DeleteCompetencyUnitRequest = {
            competencyUnitId,
            active: false,
        };
        return this.deleteCompetencyUnits(competencyModuleId, [deleteRequest]);
    }

    // api call to mark/unmark competency module as favourite
    updateCompetencyModuleFavourite(favouriteRequest: FavouriteRequest): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http
            .put(`${this.cdmsApi}competencymodules/favourite`, [favouriteRequest], { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // api call to mark/unmark competency unit as favourite
    updateCompetencyUnitFavourite(favouriteRequest: FavouriteRequest): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http
            .put(`${this.cdmsApi}competencyunits/favourite`, [favouriteRequest], { headers: this.headers })
            .pipe(catchError(this.handleError.bind(this)));
    }

    // api call to search persons by name
    searchPersons(search: string, competencyUnitId: string): Observable<Person[]> {
        this.addPostAndPutHeaders();
        // const params = new HttpParams().set('search', search);
        const payload = {
            competencyUnitId: competencyUnitId,
            search: search,
        };
        // return this.http.get<APIRes<{ total: number; data: Person[] }>>(`${this.cdmsApi}persons/searchpersonname`, { headers: this.headers, params }).pipe(
        //     map((res) => res?.response?.data || []),
        //     catchError(this.handleError.bind(this))
        // );
        return this.http.post(`${this.cdmsApi}persons/searchpersonname`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to create enrollments
    createEnrollment(payload: EnrollmentCreateRequest[]): Observable<any> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.cdmsApi}enrollments`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    // api call to deactivate enrollments
    deactivateEnrollments(payload: DeleteEnrollmentRequest[]): Observable<any> {
        this.addPostAndPutHeaders();
        return this.http.put(`${this.cdmsApi}enrollments`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getRegions(): Observable<APIRes<RegionOption[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}regions`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    getRegionHierarchy(): Observable<APIRes<any[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}regions/getregionhierarchy`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    createRegions(payload: Array<{ name: string }>): Observable<APIRes<any>> {
        return this.postLookupChanges('regions', payload);
    }

    updateRegions(payload: Array<{ regionId: string; name: string }>): Observable<APIRes<any>> {
        return this.putLookupChanges('regions', payload);
    }

    deleteRegions(payload: Array<{ regionId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('regions', payload);
    }

    // api call to get contract statuses
    getContractStatuses(): Observable<APIRes<any[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}contractstatuses`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    createContractStatuses(payload: Array<{ name: string; comments?: string }>): Observable<APIRes<any>> {
        return this.postLookupChanges('contractstatuses', payload);
    }

    updateContractStatuses(payload: Array<{ contractStatusId: string; name: string; comments?: string }>): Observable<APIRes<any>> {
        return this.putLookupChanges('contractstatuses', payload);
    }

    deleteContractStatuses(payload: Array<{ contractStatusId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('contractstatuses', payload);
    }

    // api call to get job titles
    getJobTitles(): Observable<APIRes<any[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}jobtitles`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    createJobTitles(payload: Array<{ name: string }>): Observable<APIRes<any>> {
        return this.postLookupChanges('jobtitles', payload);
    }

    updateJobTitles(payload: Array<{ jobTitleId: string; name: string }>): Observable<APIRes<any>> {
        return this.putLookupChanges('jobtitles', payload);
    }

    deleteJobTitles(payload: Array<{ jobTitleId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('jobtitles', payload);
    }

    // api call to get skills/main skills
    getSkills(): Observable<APIRes<any[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}skills`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    createSkills(payload: Array<{ name: string }>): Observable<APIRes<any>> {
        return this.postLookupChanges('skills', payload);
    }

    updateSkills(payload: Array<{ skillId: string; name: string }>): Observable<APIRes<any>> {
        return this.putLookupChanges('skills', payload);
    }

    deleteSkills(payload: Array<{ skillId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('skills', payload);
    }

    getBases(): Observable<APIRes<any[]>> {
        return this.getLocationsByType(1);
    }

    // api call to get training/assessment venues (locations with type 2)
    getTrainingVenues(): Observable<APIRes<any[]>> {
        return this.getLocationsByType(2);
    }

    createLocations(payload: Array<{ name: string; countryId: string; locationType: number }>): Observable<APIRes<any>> {
        return this.postLookupChanges('locations', payload);
    }

    updateLocations(payload: Array<{ locationId: string; name: string; countryId: string; locationType: number }>): Observable<APIRes<any>> {
        return this.putLookupChanges('locations', payload);
    }

    deleteLocations(payload: Array<{ locationId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('locations', payload);
    }

    getAllCountries(): Observable<APIRes<CountryOption[]>> {
        return this.getRegions().pipe(
            switchMap((regionsResponse) => {
                const regions = regionsResponse?.response || [];
                if (!regions.length) {
                    return of({ success: true, response: [] } as APIRes<CountryOption[]>);
                }

                return forkJoin(regions.map((region) => this.getCountriesByRegion(region.regionId))).pipe(
                    map((responses) => {
                        const countries = responses.reduce((accumulator: CountryOption[], response) => {
                            const items = response?.response || [];
                            items.forEach((item) => accumulator.push(item));
                            return accumulator;
                        }, []);

                        return {
                            success: true,
                            response: countries,
                        } as APIRes<CountryOption[]>;
                    })
                );
            }),
            catchError(this.handleError.bind(this))
        );
    }

    getCountries(): Observable<APIRes<CountryOption[]>> {
        this.addPostAndPutHeaders();
        return this.http.get(`${this.cdmsApi}countries`, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    private getLocationsByType(locationType: number): Observable<APIRes<any[]>> {
        this.addPostAndPutHeaders();
        const params = new HttpParams().set('locationType', locationType.toString());
        return this.http.get(`${this.cdmsApi}locations`, { headers: this.headers, params }).pipe(catchError(this.handleError.bind(this)));
    }

    getCountriesByRegion(regionId: string): Observable<APIRes<CountryOption[]>> {
        this.addPostAndPutHeaders();
        const params = new HttpParams().set('regionId', regionId);
        return this.http.get(`${this.cdmsApi}countries`, { headers: this.headers, params }).pipe(catchError(this.handleError.bind(this)));
    }

    createCountries(payload: Array<{ name: string; regionId: string; code?: string }>): Observable<APIRes<any>> {
        return this.postLookupChanges('countries', payload);
    }

    createCountriesInRegions(payload: any[]): Observable<APIRes<any>> {
        return this.postLookupChanges('countries', payload);
    }

    updateCountries(payload: Array<{ countryId: string; name: string; regionId: string; code?: string }>): Observable<APIRes<any>> {
        return this.putLookupChanges('countries', payload);
    }

    updateCountriesInRegions(payload: any[]): Observable<APIRes<any>> {
        return this.putLookupChanges('countries', payload);
    }

    deleteCountries(payload: Array<{ countryId: string; active: boolean }>): Observable<APIRes<any>> {
        return this.putLookupChanges('countries', payload);
    }

    deleteCountriesInRegions(payload: any[]): Observable<APIRes<any>> {
        return this.putLookupChanges('countries', payload);
    }

    createBasesInRegions(payload: any[]): Observable<APIRes<any>> {
        return this.postLookupChanges('regions', payload);
    }

    updateBasesInRegions(payload: any[]): Observable<APIRes<any>> {
        return this.putLookupChanges('regions', payload);
    }

    deleteBasesInRegions(payload: any[]): Observable<APIRes<any>> {
        return this.putLookupChanges('regions', payload);
    }

    getCompaniesByCountry(countryId: string): Observable<APIRes<{ total: number; data: CompanyOption[] }>> {
        this.addPostAndPutHeaders();
        const params = new HttpParams().set('countryId', countryId);
        return this.http.get(`${this.cdmsApi}companies`, { headers: this.headers, params }).pipe(catchError(this.handleError.bind(this)));
    }

    private postLookupChanges<TRequest>(endpoint: string, payload: TRequest[]): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http.post(`${this.cdmsApi}${endpoint}`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    private putLookupChanges<TRequest>(endpoint: string, payload: TRequest[]): Observable<APIRes<any>> {
        this.addPostAndPutHeaders();
        return this.http.put(`${this.cdmsApi}${endpoint}`, payload, { headers: this.headers }).pipe(catchError(this.handleError.bind(this)));
    }

    addPostAndPutHeaders() {
        this.headers = new HttpHeaders();
        this.headers = this.headers.append('Ocp-Apim-Subscription-Key', this.subVal);
        this.headers = this.headers.append('content-type', 'application/json');
        this.headers = this.headers.append('Accept', 'application/json');
        this.headers = this.headers.append('Access-Control-Allow-Origin', '*');
        this.headers = this.headers.append('Content-Type', 'multipart/form-data');
        this.headers = this.headers.append('Authorization', `Bearer ${localStorage.getItem('msal.accessToken')}`);
    }

    private handleError(error: any) {
        this.logedInUserId = this.authService?.instance?.getActiveAccount()?.idTokenClaims?.oid;
        let currOrg = localStorage.getItem('selectedOrgId');
        let currInstance = localStorage.getItem('currentInstanceId');
        if (error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.message);

            // appInsights.trackException({
            //     exception: new Error(error.message),
            //     properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId },
            // });
            // appInsights.trackPageView({
            //     properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId },
            // });
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.
            console.error(`Backend returned code ${error.status}, ` + `body was: ${error.message}`);
            //     appInsights.trackException({
            //         exception: new Error(error.message),
            //         properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId },
            //     });
            //     appInsights.trackPageView({ properties: { currOrg: currOrg, currentInstanceId: currInstance, oid: this.logedInUserId } });
        }
        // Return an observable with a user-facing error message.
        return throwError('Something bad happened; please try again later.');
    }
}
